import '@mdxeditor/editor/style.css'

import {
  BoldItalicUnderlineToggles,
  CreateLink,
  directivesPlugin,
  headingsPlugin,
  insertDirective$,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  Separator,
  toolbarPlugin,
  UndoRedo,
  usePublisher
} from '@mdxeditor/editor'
import { useEffect, useMemo, useRef } from 'react'

import styles from './RichTextEditor.module.scss'

const TOKENS = [
  { key: 'guestName', label: 'First name' },
  { key: 'guestLastName', label: 'Last name' },
  // { key: 'hotelName', label: 'Hotel name' },
  { key: 'checkoutDate', label: 'Checkout date' }
]

function TokenButton({ token }) {
  const insertDirective = usePublisher(insertDirective$)
  return (
    <button
      type="button"
      onClick={() =>
        insertDirective({
          type: 'textDirective',
          name: 'token',
          attributes: token, // {key,label}
          children: []
        })
      }
      style={{
        //background: '#fcd398',
        textAlign: 'center',
        //border: '1px solid #fcd398',
        border: '1px solid #e0e0e0',
        background: 'white',
        borderRadius: 3,
        color: '#3c3a3a',
        fontSize: 12,
        height: 28
      }}
      title={`Insert ${token.label}`}
    >
      {token.label}
    </button>
  )
}

function TokenPicker() {
  const insertDirective = usePublisher(insertDirective$)
  return (
    <select
      defaultValue=""
      onChange={e => {
        const t = TOKENS.find(x => x.key === e.target.value)
        if (!t) return
        insertDirective({
          type: 'textDirective',
          name: 'token',
          attributes: t,
          children: []
        })
        e.target.selectedIndex = 0
      }}
      style={{
        //background: '#fcd398',
        textAlign: 'center',
        //border: '1px solid #fcd398',
        border: '1px solid #e0e0e0',
        background: 'white',
        borderRadius: 3,
        color: '#3c3a3a',
        fontSize: 12,
        height: 28
      }}
    >
      <option value="" disabled>
        + Tags
      </option>
      {TOKENS.map(t => (
        <option key={t.key} value={t.key}>
          {t.label}
        </option>
      ))}
    </select>
  )
}

function TokenChip({ mdastNode }) {
  const attrs = (mdastNode && mdastNode.attributes) || {}
  const { key, label } = attrs

  return (
    <span
      contentEditable={false}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 6px',
        borderRadius: 6,
        border: '1px solid #ccc',
        fontSize: 12,
        lineHeight: '20px',
        whiteSpace: 'nowrap',
        userSelect: 'none'
      }}
      title={key ? `{{${key}}}` : 'Token'}
    >
      {label || key || 'Token'}
    </span>
  )
}

// Describe our inline directive
const AtomicTokenDescriptor = {
  name: 'token',
  testNode: node => node.type === 'textDirective' && node.name === 'token',
  attributes: ['key', 'label'],
  hasChildren: false,
  Editor: TokenChip
}

// Simple toolbar button to insert a token
function InsertTokenButton() {
  const insertDirective = usePublisher(insertDirective$)

  const handleClick = () => {
    insertDirective({
      type: 'textDirective',
      name: 'token',
      attributes: { key: 'firstName', label: 'First name' },
      children: []
    })
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 6 }}
      title="Insert token"
    >
      + Token
    </button>
  )
}

const RichTextEditor = ({ label, value, onChange, editorKey = 'richtext' }) => {
  const editorRef = useRef(null)
  const lastAppliedMarkdownRef = useRef('')

  useEffect(() => {
    const editor = editorRef.current
    if (!editor) {
      return
    }

    const nextValue = value || ''
    if (nextValue === lastAppliedMarkdownRef.current) {
      return
    }

    editor.setMarkdown(nextValue)
    lastAppliedMarkdownRef.current = nextValue
  }, [value, editorKey])

  const plugins = useMemo(
    () => [
      headingsPlugin({ allowedHeadingLevels: [1, 2, 3, 4] }),
      listsPlugin(),
      linkPlugin(),
      linkDialogPlugin(),
      directivesPlugin({ directiveDescriptors: [AtomicTokenDescriptor] }),
      toolbarPlugin({
        toolbarPosition: 'bottom',
        toolbarClassName: styles.richTextEditorToolbar,
        toolbarContents: () => (
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', paddingBottom: 8, paddingTop: 5 }}>
              <UndoRedo />
              <Separator />
              <BoldItalicUnderlineToggles />
              <Separator />
              <ListsToggle options={['bullet']} />
              <CreateLink />
            </div>
            <div
              style={{
                display: 'flex',
                gap: 5,
                paddingLeft: 8,
                paddingTop: 10,
                paddingBottom: 5,
                borderTop: '1px solid #dedede',
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              {TOKENS.map(t => (
                <TokenButton key={t.key} token={t} />
              ))}
            </div>
          </div>
        )
      })
    ],
    []
  )

  return (
    <>
      {label && <span className={styles.inputLabel}>{label}</span>}
      <div className={styles.richTextEditorContainer}>
        <MDXEditor
          key={editorKey}
          ref={editorRef}
          markdown={value || ''}
          onChange={markdown => {
            lastAppliedMarkdownRef.current = markdown
            onChange(markdown)
          }}
          className={styles.richTextEditor}
          plugins={plugins}
        />
      </div>
    </>
  )
}

export default RichTextEditor
